/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Inject, Injectable } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { GtoUom, uomQuantityComparator } from '@gh/builder-shared-data';
import {
  createDefaultGridModel,
  dictionaryValueFormatter,
  EDITABLE_CELL_GREEN_CLASS,
  likeEditableCellRenderer,
  ROW_DIRTY_CLASS,
} from '@gh/common-internal-shared';
import { EnumDictionary, getDialogInitData } from '@gh/common-shared';
import { TranslationService } from '@gh/core-mls';
import {
  buildEditableQuantityColumn,
  buildQuantityColumn,
  caseInsensitiveGridComparator,
  gridEditableValueCollectionComparator,
  GridModel,
  ManualGridDefinitionWithModel,
} from '@gh/core-ui-grid';
import { ColDef, GridOptions } from 'ag-grid-community';
import { isNil } from 'lodash';
import {
  getQuoteHousePlanTopId,
  getQuoteHpTopQuantityType,
  QuoteHousePlanTop,
  quoteHousePlanTopFieldState,
  validateQuoteHousePlanTopField,
} from '../../resources/quote.model';
import { QuoteTopDialogCommandService } from './quote-top-dialog-command.service';
import { FormMode } from '@gh/core-ui';

@Injectable()
export class QuoteTopDialogGridDefinition extends ManualGridDefinitionWithModel {
  private gtoUomDict: EnumDictionary<GtoUom>;

  constructor(@Inject(MAT_DIALOG_DATA) private dialogData: any,
              private translationService: TranslationService,
              private commands: QuoteTopDialogCommandService) {
    super();

    this.gtoUomDict = getDialogInitData(dialogData)['gtoUomDict'];
  }

  buildGridModel(): GridModel {
    return createDefaultGridModel({
      getId: getQuoteHousePlanTopId,
      getCellDef: quoteHousePlanTopFieldState,
      validate: validateQuoteHousePlanTopField,
      command: (entity, name, value) => this.commands.setTopValue(getQuoteHousePlanTopId(entity), value),
    });
  }

  buildGridOptions(): GridOptions {
    return {
      rowClassRules: {
        [ROW_DIRTY_CLASS]: ({ data }: { data: QuoteHousePlanTop }) => !isNil(data.dirtyType),
      },
    };
  }

  buildColumnDefs(): ColDef[] {
    return [
      {
        field: 'code',
        headerName: this.translationService.get('lbl_code'),
        width: 750,
        comparator: caseInsensitiveGridComparator,
      },
      {
        field: 'baseValue',
        headerName: this.translationService.get('lbl_base_value'),
        width: 170,
        ...buildQuantityColumn(getQuoteHpTopQuantityType),
        cellRenderer: likeEditableCellRenderer,
        cellClassRules: {
          [EDITABLE_CELL_GREEN_CLASS]: ({ data }: { data: QuoteHousePlanTop }) => data.isFromGto,
        },
      },
      {
        field: 'uomCode',
        headerName: this.translationService.get('lbl_uom_abbreviation'),
        width: 130,
        valueFormatter: dictionaryValueFormatter(this.gtoUomDict),
      },
      {
        field: 'value',
        headerName: this.translationService.get('lbl_value'),
        width: 100,
        hide: this.commands.formMode === FormMode.ViewMode,
        ...buildEditableQuantityColumn(getQuoteHpTopQuantityType, this.model.getCellAccessor('value')),
        comparator: gridEditableValueCollectionComparator(uomQuantityComparator),
      },
      {
        field: 'value',
        headerName: this.translationService.get('lbl_value'),
        width: 100,
        hide: this.commands.formMode !== FormMode.ViewMode,
        ...buildQuantityColumn(getQuoteHpTopQuantityType),
        comparator: gridEditableValueCollectionComparator(uomQuantityComparator),
      },
    ];
  }
}
